/**
 * Centralized API URL builder — Single Source of Truth for every
 * backend endpoint the frontend reaches.
 *
 * `apiOrigin()` resolves to the configured backend origin. Every
 * service / auth redirect / dynamic path should route through
 * `apiUrl()` or the `API` map below so we never scatter
 * `import.meta.env.VITE_API_URL` across the codebase again.
 *
 * Resolution rules:
 *   VITE_API_URL set  → absolute URL, cross-origin to that host.
 *                       Dev default: http://localhost:8000.
 *                       Prod default: https://chiefy.ai — matches
 *                       the two-app Azure topology (Vite SPA at
 *                       app.chiefy.ai hits Django at chiefy.ai).
 *   VITE_API_URL empty → same-origin (relative). Useful behind a
 *                       reverse proxy (Azure Front Door, Caddy,
 *                       nginx) that fronts both FE + BE on one
 *                       origin — the bundle runs unchanged on any
 *                       host, the edge just routes `/api/*` itself.
 *
 * Registry layout:
 *   API.auth        — CSRF / token / login
 *   API.user        — profile, prefs, sessions, export, delete
 *   API.chats       — assistant threads + SSE streaming
 *   API.projects    — project CRUD + files
 *   API.networking  — connections / groups / DMs / blocks / reports
 *   API.notifications — bell + preferences
 *   API.education   — courses / quizzes / enrollment
 *   API.bootcamp    — program / weeks / benchmark / evaluation
 *                     (+ admin review queue)
 *   API.mentorship  — submissions / insights / meetings / reports /
 *                     role requests / learning updates / feedback
 *   API.admin       — dashboard / users / grants / flags / abuse
 *   API.usage       — me + admin token-usage telemetry
 *   API.billing     — plans / portal / invoices / checkout
 *   API.search      — cross-surface search
 *
 * Every value is rebuilt from `apiOrigin()` at call time (via
 * getters) so the env-var fallback stays authoritative.
 */

export function apiOrigin(): string {
    const raw = import.meta.env.VITE_API_URL;
    if (typeof raw !== 'string') return '';
    // Trim + strip trailing slashes so `apiUrl('/foo')` never
    // produces `//foo` when the env var ends with `/`.
    return raw.trim().replace(/\/+$/, '');
}

export function apiUrl(path: string): string {
    const origin = apiOrigin();
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${p}`;
}

export const API = {
    // ── Auth ───────────────────────────────────────────────────────────
    auth: {
        get csrf()  { return apiUrl('/api/auth/csrf/'); },
        get token() { return apiUrl('/api/auth/token/'); },
        login: (next: string) => apiUrl(`/accounts/login/?next=${encodeURIComponent(next)}`),
        get email() { return apiUrl('/accounts/email/'); },
    },

    // ── User ───────────────────────────────────────────────────────────
    user: {
        get me()            { return apiUrl('/api/user/'); },
        get profile()       { return apiUrl('/api/profile/'); },
        get updateName()    { return apiUrl('/api/user/update-name/'); },
        get preferences()   { return apiUrl('/api/user/preferences/'); },
        get sessions()      { return apiUrl('/api/user/sessions/'); },
        revokeSession: (id: string) => apiUrl(`/api/user/sessions/${id}/revoke/`),
        get identity()      { return apiUrl('/api/user/identity/'); },
        get export()        { return apiUrl('/api/user/export/'); },
        get delete()        { return apiUrl('/api/user/delete/'); },
        get billingPortal() { return apiUrl('/api/user/billing-portal/'); },
        get backgrounds()   { return apiUrl('/api/backgrounds/'); },
    },

    // ── Assistant chats ────────────────────────────────────────────────
    chats: {
        get list()        { return apiUrl('/api/chats/'); },
        get direct()      { return apiUrl('/api/chats/?has_project=false'); },
        detail:   (id: string) => apiUrl(`/api/chats/${id}/`),
        messages: (id: string) => apiUrl(`/api/chats/${id}/messages/`),
        regenerate: (id: string) => apiUrl(`/api/chats/${id}/messages/regenerate/`),
    },

    // ── Projects ───────────────────────────────────────────────────────
    projects: {
        get list()      { return apiUrl('/api/projects/'); },
        detail:  (id: string) => apiUrl(`/api/projects/${id}/`),
        chats:   (id: string) => apiUrl(`/api/projects/${id}/chats/`),
        file:    (fileId: string) => apiUrl(`/api/files/${fileId}/`),
        get phases()    { return apiUrl('/api/phases/'); },
    },

    // ── Networking (people + groups) ───────────────────────────────────
    networking: {
        get professionals() { return apiUrl('/api/networking/professionals/'); },
        professional: (userId: string | number) => apiUrl(`/api/networking/professionals/${userId}/`),
        mutual: (userId: string | number) => apiUrl(`/api/networking/professionals/${userId}/mutual/`),
        peopleYouMayKnow: (page?: number) =>
            apiUrl(`/api/networking/people_you_may_know/${page ? `?page=${page}` : ''}`),

        get connections() { return apiUrl('/api/networking/connections/'); },
        connection: (id: string) => apiUrl(`/api/networking/connections/${id}/`),
        acceptConnection:  (id: string) => apiUrl(`/api/networking/connections/${id}/accept/`),
        declineConnection: (id: string) => apiUrl(`/api/networking/connections/${id}/decline/`),
        cancelConnection:  (id: string) => apiUrl(`/api/networking/connections/${id}/cancel/`),
        get requestConnection() { return apiUrl('/api/networking/connections/request/'); },
        get connectionRequests() { return apiUrl('/api/networking/connections/requests/'); },

        get groups() { return apiUrl('/api/networking/groups/'); },
        group: (id: string) => apiUrl(`/api/networking/groups/${id}/`),
        groupMembers: (id: string) => apiUrl(`/api/networking/groups/${id}/members/`),
        addGroupMembers: (id: string) => apiUrl(`/api/networking/groups/${id}/members/add/`),
        removeGroupMember: (id: string, userId: string | number) =>
            apiUrl(`/api/networking/groups/${id}/members/${userId}/`),
        leaveGroup:    (id: string) => apiUrl(`/api/networking/groups/${id}/leave/`),
        transferGroup: (id: string) => apiUrl(`/api/networking/groups/${id}/transfer_ownership/`),
        groupMessages: (id: string) => apiUrl(`/api/networking/groups/${id}/messages/`),
        groupReadAll:  (id: string) => apiUrl(`/api/networking/groups/${id}/read_all/`),
        groupMessage:  (messageId: string) => apiUrl(`/api/networking/groups/messages/${messageId}/`),
        get createGroup() { return apiUrl('/api/networking/groups/create/'); },

        dm: (peerId: string | number) => apiUrl(`/api/networking/messages/${peerId}/`),
        dmReadAll: (peerId: string | number) => apiUrl(`/api/networking/messages/${peerId}/read_all/`),
        dmDetail:  (messageId: string) => apiUrl(`/api/networking/messages/detail/${messageId}/`),
        get conversations() { return apiUrl('/api/networking/conversations/'); },

        get profile() { return apiUrl('/api/networking/profile/'); },
        get profileAvatar() { return apiUrl('/api/networking/profile/avatar/'); },

        get blocks() { return apiUrl('/api/networking/blocks/'); },
        blockUser:   (userId: string | number) => apiUrl(`/api/networking/users/${userId}/block/`),
        unblockUser: (userId: string | number) => apiUrl(`/api/networking/users/${userId}/unblock/`),
        reportUser:  (userId: string | number) => apiUrl(`/api/networking/users/${userId}/report/`),

        adminAbuseReports: apiUrl('/api/networking/admin/abuse_reports/'),
        actionAbuseReport: (reportId: string) =>
            apiUrl(`/api/networking/admin/abuse_reports/${reportId}/action/`),
    },

    // ── Notifications (cross-app bell) ─────────────────────────────────
    notifications: {
        get list()        { return apiUrl('/api/notifications/'); },
        markRead: (id: string) => apiUrl(`/api/notifications/${id}/read/`),
        get readAll()     { return apiUrl('/api/notifications/read_all/'); },
        get readMany()    { return apiUrl('/api/notifications/read_many/'); },
        get unreadSummary() { return apiUrl('/api/notifications/unread_summary/'); },
        get preferences() { return apiUrl('/api/notifications/preferences/'); },
        preferenceScope: (scope: string) =>
            apiUrl(`/api/notifications/preferences/${encodeURIComponent(scope)}/`),
    },

    // ── Education (standalone courses — distinct from Bootcamp) ───────
    education: {
        get courses() { return apiUrl('/api/courses/'); },
        coursesByType: (courseType: string) => apiUrl(`/api/courses/?course_type=${courseType}`),
        enroll:  (courseId: string) => apiUrl(`/api/courses/${courseId}/enroll/`),
        lessons: (courseId: string) => apiUrl(`/api/courses/${courseId}/lessons/`),
        lessonComplete: (courseId: string, lessonId: string) =>
            apiUrl(`/api/courses/${courseId}/lessons/${lessonId}/complete/`),
        progress:    (courseId: string) => apiUrl(`/api/courses/${courseId}/progress/`),
        quizzes:     (courseId: string) => apiUrl(`/api/courses/${courseId}/quizzes/`),
        certificate: (courseId: string) => apiUrl(`/api/courses/${courseId}/certificate/`),
        quiz:         (quizId: string) => apiUrl(`/api/quizzes/${quizId}/`),
        quizSubmit:   (quizId: string) => apiUrl(`/api/quizzes/${quizId}/submit/`),
        quizAttempts: (quizId: string) => apiUrl(`/api/quizzes/${quizId}/attempts/`),
        get enrollments() { return apiUrl('/api/enrollments/'); },
    },

    // ── Bootcamp (8-week program + facilitator review queue) ──────────
    bootcamp: {
        get program()    { return apiUrl('/api/bootcamp/'); },
        get enroll()     { return apiUrl('/api/bootcamp/enroll/'); },
        get withdraw()   { return apiUrl('/api/bootcamp/withdraw/'); },
        get benchmark()  { return apiUrl('/api/bootcamp/benchmark/'); },
        get evaluation() { return apiUrl('/api/bootcamp/evaluation/'); },
        week:       (weekNumber: number | string) => apiUrl(`/api/bootcamp/weeks/${weekNumber}/`),
        weekPrep:   (weekNumber: number | string) => apiUrl(`/api/bootcamp/weeks/${weekNumber}/prep/`),
        weekDeliverables: (weekNumber: number | string) =>
            apiUrl(`/api/bootcamp/weeks/${weekNumber}/deliverables/`),
        get adminReviewQueue() { return apiUrl('/api/bootcamp/admin/review_queue/'); },
        adminReview: (enrollmentId: string, weekNumber: number | string) =>
            apiUrl(`/api/bootcamp/admin/reviews/${enrollmentId}/${weekNumber}/`),
    },

    // ── Mentorship ────────────────────────────────────────────────────
    mentorship: {
        get projects() { return apiUrl('/api/mentorship/projects/'); },
        project:  (id: string) => apiUrl(`/api/mentorship/projects/${id}/`),
        archiveProject:   (id: string) => apiUrl(`/api/mentorship/projects/${id}/archive/`),
        unarchiveProject: (id: string) => apiUrl(`/api/mentorship/projects/${id}/unarchive/`),
        reassignMentor:   (id: string) => apiUrl(`/api/mentorship/projects/${id}/reassign_mentor/`),
        projectRisks:     (id: string) => apiUrl(`/api/mentorship/projects/${id}/risks/`),

        get submissions() { return apiUrl('/api/mentorship/submissions/'); },
        submission:       (id: string) => apiUrl(`/api/mentorship/submissions/${id}/`),
        assignSubmission: (id: string) => apiUrl(`/api/mentorship/submissions/${id}/assign/`),
        retryAssessment:  (id: string) => apiUrl(`/api/mentorship/submissions/${id}/retry_assessment/`),

        assessment:           (id: string) => apiUrl(`/api/mentorship/assessments/${id}/`),
        assessmentReview:     (id: string) => apiUrl(`/api/mentorship/assessment_reviews/${id}/`),
        regenerateAssessmentReview: (id: string) =>
            apiUrl(`/api/mentorship/assessment_reviews/${id}/regenerate/`),
        feedbackTemplate:     (id: string) => apiUrl(`/api/mentorship/feedback_templates/${id}/`),
        regenerateFeedback:   (id: string) =>
            apiUrl(`/api/mentorship/feedback_templates/${id}/regenerate/`),

        get insights() { return apiUrl('/api/mentorship/insights/'); },
        insight: (id: string) => apiUrl(`/api/mentorship/insights/${id}/`),
        insightFeedback: (id: string) => apiUrl(`/api/mentorship/insights/${id}/feedback/`),

        get meetings() { return apiUrl('/api/mentorship/meetings/'); },
        meeting:       (id: string) => apiUrl(`/api/mentorship/meetings/${id}/`),
        cancelMeeting: (id: string) => apiUrl(`/api/mentorship/meetings/${id}/cancel/`),
        meetingIcs:    (id: string) => apiUrl(`/api/mentorship/meetings/${id}/ics/`),
        markMeetingOccurred: (id: string) => apiUrl(`/api/mentorship/meetings/${id}/mark_occurred/`),
        markMeetingMissed:   (id: string) => apiUrl(`/api/mentorship/meetings/${id}/mark_missed/`),

        get reports() { return apiUrl('/api/mentorship/reports/'); },
        report:        (id: string) => apiUrl(`/api/mentorship/reports/${id}/`),
        disputeReport: (id: string) => apiUrl(`/api/mentorship/reports/${id}/dispute/`),

        get disputes() { return apiUrl('/api/mentorship/disputes/'); },
        resolveDispute:  (id: string) => apiUrl(`/api/mentorship/disputes/${id}/resolve/`),
        withdrawDispute: (id: string) => apiUrl(`/api/mentorship/disputes/${id}/withdraw/`),

        get roleRequests() { return apiUrl('/api/mentorship/role_requests/'); },
        approveRoleRequest: (id: string) => apiUrl(`/api/mentorship/role_requests/${id}/approve/`),
        rejectRoleRequest:  (id: string) => apiUrl(`/api/mentorship/role_requests/${id}/reject/`),

        get learningUpdates() { return apiUrl('/api/mentorship/learning_updates/'); },
        runLearningUpdates: apiUrl('/api/mentorship/learning_updates/run/'),
        applyLearningUpdate: (id: string) => apiUrl(`/api/mentorship/learning_updates/${id}/apply/`),

        get engagementSummaries() { return apiUrl('/api/mentorship/engagement_summaries/'); },
        engagementSnapshots: (userId: string | number) =>
            apiUrl(`/api/mentorship/engagement_summaries/snapshots/?user_id=${userId}`),

        get risks() { return apiUrl('/api/mentorship/risks/'); },
        risk: (id: string) => apiUrl(`/api/mentorship/risks/${id}/`),

        get clientProfiles() { return apiUrl('/api/mentorship/client_profiles/'); },
        clientProfile: (id: string) => apiUrl(`/api/mentorship/client_profiles/${id}/`),

        // Mentality chat (AI coaching assistant for mentees)
        get mentalityChat()        { return apiUrl('/api/mentorship/mentality_chat/'); },
        get mentalityChatHistory() { return apiUrl('/api/mentorship/mentality_chat/history/'); },
        get mentalityChatUpload()  { return apiUrl('/api/mentorship/mentality_chat/upload/'); },
        get mentalityContext()     { return apiUrl('/api/mentorship/mentality_context/'); },
        get mentalityDocument()    { return apiUrl('/api/mentorship/mentality_document/'); },

        // Mentor user management
        get users() { return apiUrl('/api/mentorship/users/'); },
        userAuditLog: (userId: string | number, limit?: number) =>
            apiUrl(`/api/mentorship/users/${userId}/audit_log/${limit ? `?limit=${limit}` : ''}`),
        userCapacity:    (userId: string | number) => apiUrl(`/api/mentorship/users/${userId}/capacity/`),
        userMentor:      (userId: string | number) => apiUrl(`/api/mentorship/users/${userId}/mentor/`),
        userRole:        (userId: string | number) => apiUrl(`/api/mentorship/users/${userId}/role/`),
        userSpecialties: (userId: string | number) => apiUrl(`/api/mentorship/users/${userId}/specialties/`),
    },

    // ── Admin (dashboard + platform ops) ──────────────────────────────
    admin: {
        get dashboard() { return apiUrl('/api/admin/dashboard/'); },
        get users()     { return apiUrl('/api/admin/users/'); },
        user:   (userId: string | number) => apiUrl(`/api/admin/users/${userId}/`),
        grants: (userId: string | number) => apiUrl(`/api/admin/users/${userId}/grants/`),
        grant:  (userId: string | number, grantId: string) =>
            apiUrl(`/api/admin/users/${userId}/grants/${grantId}/`),
        get flags() { return apiUrl('/api/flags/'); },
    },

    // ── Usage (token telemetry) ───────────────────────────────────────
    usage: {
        get summary() { return apiUrl('/api/me/usage/summary/'); },
        daily: (days: number) => apiUrl(`/api/me/usage/daily/?days=${days}`),
        byChat: (limit: number, days: number) =>
            apiUrl(`/api/me/usage/by-chat/?limit=${limit}&days=${days}`),
        admin: {
            summary:  (days: number) => apiUrl(`/api/admin/usage/summary/?days=${days}`),
            daily:    (days: number) => apiUrl(`/api/admin/usage/daily/?days=${days}`),
            byModel:  (days: number) => apiUrl(`/api/admin/usage/by-model/?days=${days}`),
            topUsers: (days: number, limit: number, sort: string) =>
                apiUrl(`/api/admin/usage/top-users/?days=${days}&limit=${limit}&sort=${sort}`),
        },
    },

    // ── Billing (entitlements app mounted at /billing/) ───────────────
    billing: {
        get plans()     { return apiUrl('/api/plans/'); },
        get portal()    { return apiUrl('/api/user/billing-portal/'); },
        get invoices()  { return apiUrl('/billing/api/invoices/'); },
        get checkout()  { return apiUrl('/billing/api/checkout/'); },
    },

    // ── Search ────────────────────────────────────────────────────────
    search: (q: string) => apiUrl(`/api/search/?q=${encodeURIComponent(q.trim())}`),

    /** Resolve a path to an absolute URL. Accepts already-absolute
     *  URLs unchanged (e.g. paginated `next` links from DRF). Use for
     *  paths not covered by the registry above — e.g. one-off routes
     *  or dynamic paths from server payloads. */
    resolve: (path: string) => (path.startsWith('http') ? path : apiUrl(path)),
} as const;
